import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, MessageSquare, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { CONTACT_IDS } from "@/constants/testIds/site";

const EMPTY = { name: "", phone: "", email: "", subject: "", message: "" };

export default function ContactForm() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const submit = async (ev) => {
    ev.preventDefault();
    const e = {};
    if (!form.name || form.name.trim().length < 2) e.name = "Enter your name";
    if (!/^\+?\d{7,15}$/.test((form.phone || "").replace(/\s+/g, ""))) e.phone = "Enter a valid phone";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.message || form.message.trim().length < 5) e.message = "Message is required";
    setErrors(e);
    if (Object.keys(e).length) return;

    setBusy(true);
    try {
      await api.post("/contacts", {
        ...form,
        email: form.email || null,
        subject: form.subject || null,
      });
      setDone(true);
      setForm(EMPTY);
      toast.success("Message sent — we'll get back to you.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid={CONTACT_IDS.success} className="bg-white rounded-3xl border border-[#E2E8F0] p-10 text-center shadow-sm">
        <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 grid place-items-center mx-auto"><CheckCircle2 className="w-7 h-7" /></div>
        <h3 className="mt-4 font-display text-xl font-bold">Message sent</h3>
        <p className="mt-1 text-[#6B7280]">Thanks — we'll respond as soon as we can.</p>
        <Button onClick={() => setDone(false)} className="mt-5 bg-[#0057B8] hover:bg-[#003F87] text-white rounded-full btn-brand">Send another</Button>
      </div>
    );
  }

  const fieldCls = "bg-white border-[#E2E8F0] focus-visible:ring-[#0057B8] focus-visible:ring-2 focus-visible:ring-offset-2";

  return (
    <form onSubmit={submit} data-testid={CONTACT_IDS.form} className="bg-white rounded-3xl border border-[#E2E8F0] p-6 sm:p-8 shadow-sm">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-[#0057B8]/10 text-[#0057B8] grid place-items-center"><MessageSquare className="w-5 h-5" /></div>
        <div>
          <h3 className="font-display text-xl font-bold text-[#111827]">Send us a message</h3>
          <p className="text-sm text-[#6B7280]">We usually reply within a few working hours.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor={CONTACT_IDS.name}>Name <span className="text-red-500">*</span></Label>
          <Input id={CONTACT_IDS.name} data-testid={CONTACT_IDS.name} value={form.name} onChange={set("name")} className={fieldCls} placeholder="Your name" />
          {errors.name && <div className="text-xs text-red-600 mt-1">{errors.name}</div>}
        </div>
        <div>
          <Label htmlFor={CONTACT_IDS.phone}>Phone <span className="text-red-500">*</span></Label>
          <Input id={CONTACT_IDS.phone} data-testid={CONTACT_IDS.phone} value={form.phone} onChange={set("phone")} className={fieldCls} placeholder="10-digit mobile" />
          {errors.phone && <div className="text-xs text-red-600 mt-1">{errors.phone}</div>}
        </div>
        <div>
          <Label htmlFor={CONTACT_IDS.email}>Email</Label>
          <Input id={CONTACT_IDS.email} data-testid={CONTACT_IDS.email} value={form.email} onChange={set("email")} className={fieldCls} placeholder="you@example.com" />
          {errors.email && <div className="text-xs text-red-600 mt-1">{errors.email}</div>}
        </div>
        <div>
          <Label htmlFor={CONTACT_IDS.subject}>Subject</Label>
          <Input id={CONTACT_IDS.subject} data-testid={CONTACT_IDS.subject} value={form.subject} onChange={set("subject")} className={fieldCls} placeholder="Enquiry / Service" />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor={CONTACT_IDS.message}>Message <span className="text-red-500">*</span></Label>
          <Textarea id={CONTACT_IDS.message} data-testid={CONTACT_IDS.message} rows={5} value={form.message} onChange={set("message")} className={fieldCls} placeholder="Tell us how we can help" />
          {errors.message && <div className="text-xs text-red-600 mt-1">{errors.message}</div>}
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button type="submit" disabled={busy} data-testid={CONTACT_IDS.submit} className="bg-[#0057B8] hover:bg-[#003F87] text-white rounded-full btn-brand h-11 px-8 active:scale-95">
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : "Send Message"}
        </Button>
      </div>
    </form>
  );
}
